import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { CargoDetailsService } from '@/services';
import type { CargoDetailsState } from '@/types/cargoList.types';

interface CargoDetailsStore extends CargoDetailsState {
  // Actions
  fetchCargoDetails: (id: number) => Promise<void>;
  fetchCargoItinerary: (id: number) => Promise<void>;
  reorderItineraryItems: (
    fixtureId: number,
    typeId: number,
    oldOrder: number,
    newOrder: number
  ) => Promise<void>;
  clearData: () => void;
  setError: (error: string | null) => void;
}

export const useCargoDetailsStore = create<CargoDetailsStore>()(
  devtools(
    (set) => ({
      // Initial state
      cargo: null,
      itinerary: [],
      loading: false,
      error: null,

      // Actions
      fetchCargoDetails: async (id: number) => {
        set({ loading: true, error: null });

        try {
          const cargo = await CargoDetailsService.getCargoById(id);
          set({ cargo, loading: false });
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to fetch cargo details';
          set({ error: errorMessage, loading: false });
        }
      },

      fetchCargoItinerary: async (id: number) => {
        try {
          const itinerary = await CargoDetailsService.getCargoItinerary(id);
          set({ itinerary });
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to fetch cargo itinerary';
          set({ error: errorMessage });
        }
      },

      clearData: () => {
        set({
          cargo: null,
          itinerary: [],
          error: null,
          loading: false,
        });
      },

      reorderItineraryItems: async (
        fixtureId: number,
        typeId: number,
        oldOrder: number,
        newOrder: number
      ) => {
        try {
          await CargoDetailsService.reorderItineraryItems(fixtureId, typeId, oldOrder, newOrder);
          // No automatic refresh - let the UI handle optimistic updates
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to reorder itinerary items';
          set({ error: errorMessage });
          throw error; // Re-throw so the page can handle error recovery
        }
      },

      setError: (error: string | null) => {
        set({ error });
      },
    }),
    { name: 'CargoDetailsStore' }
  )
);
